package com.vamvisions.main.contact.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.vamvisions.main.contact.domain.model.ContactMessage
import com.vamvisions.main.contact.domain.repository.ContactRepository
import com.vamvisions.main.core.domain.DataError
import com.vamvisions.main.core.domain.Result
import com.vamvisions.main.core.presentation.i18n.StringKey
import com.vamvisions.main.core.presentation.i18n.UiText
import com.vamvisions.main.core.presentation.i18n.toUiText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * Validates the contact form with the same rules as the server, then sends it with the optional topic. A successful
 * send clears the form, topic included; a failed one keeps the input so the user can try again.
 */
class ContactViewModel(
    private val contactRepository: ContactRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(ContactState())
    val state = _state.asStateFlow()

    fun onAction(action: ContactAction) {
        when (action) {
            is ContactAction.NameChanged -> _state.update {
                it.copy(name = action.value, nameError = it.nameError?.let { nameError(action.value) })
            }
            is ContactAction.EmailChanged -> _state.update {
                it.copy(email = action.value, emailError = it.emailError?.let { emailError(action.value) })
            }
            is ContactAction.MessageChanged -> _state.update {
                it.copy(message = action.value, messageError = it.messageError?.let { messageError(action.value) })
            }
            is ContactAction.TopicClicked -> _state.update {
                it.copy(topic = if (it.topic == action.topic) null else action.topic)
            }
            is ContactAction.WebsiteChanged -> _state.update { it.copy(website = action.value) }
            ContactAction.Send -> send()
        }
    }

    private fun send() {
        val current = _state.value
        if (current.isSending) return
        val validated = current.copy(
            nameError = nameError(current.name),
            emailError = emailError(current.email),
            messageError = messageError(current.message),
            result = null,
        )
        if (validated.nameError != null || validated.emailError != null || validated.messageError != null) {
            _state.value = validated.copy(invalidSendCount = current.invalidSendCount + 1)
            return
        }
        _state.value = validated.copy(isSending = true)
        val message = ContactMessage(
            name = current.name.trim().ifEmpty { null },
            email = current.email.trim(),
            message = current.message.trim(),
            website = current.website.ifEmpty { null },
            topic = current.topic,
        )
        viewModelScope.launch {
            when (val result = contactRepository.send(message)) {
                is Result.Success -> _state.update { ContactState(result = ContactResult.Sent, invalidSendCount = it.invalidSendCount) }
                is Result.Error -> _state.update {
                    it.copy(isSending = false, result = ContactResult.Failed(sendError(result.error)))
                }
            }
        }
    }

    /** The rate limit asks to try again later; every other failure suggests trying again or emailing directly. */
    private fun sendError(error: DataError.Network): UiText =
        if (error.type == DataError.Network.Type.TOO_MANY_REQUESTS) error.toUiText() else UiText.Res(StringKey.CONTACT_SEND_FAILED)

    private fun nameError(name: String): UiText? =
        if (name.trim().length > NAME_MAX_LENGTH) UiText.Res(StringKey.ERROR_NAME_TOO_LONG) else null

    private fun emailError(email: String): UiText? {
        val trimmed = email.trim()
        return when {
            trimmed.isEmpty() -> UiText.Res(StringKey.ERROR_FIELD_REQUIRED)
            trimmed.length > EMAIL_MAX_LENGTH || !EMAIL_PATTERN.matches(trimmed) -> UiText.Res(StringKey.ERROR_EMAIL_INVALID)
            else -> null
        }
    }

    private fun messageError(message: String): UiText? {
        val length = message.trim().length
        return when {
            length == 0 -> UiText.Res(StringKey.ERROR_MESSAGE_REQUIRED)
            length < MESSAGE_MIN_LENGTH -> UiText.Res(StringKey.ERROR_MESSAGE_TOO_SHORT)
            length > MESSAGE_MAX_LENGTH -> UiText.Res(StringKey.ERROR_MESSAGE_TOO_LONG)
            else -> null
        }
    }

    companion object {
        const val NAME_MAX_LENGTH = 100
        const val EMAIL_MAX_LENGTH = 254
        const val MESSAGE_MIN_LENGTH = 10
        const val MESSAGE_MAX_LENGTH = 5000

        /** The server's rule: an ASCII dot-atom local part (no leading, trailing or doubled dots) and a dotted domain. */
        private val EMAIL_PATTERN =
            Regex("""^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$""")
    }
}
